import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, Clock } from 'lucide-react';

const DrillDownPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const filterType = searchParams.get('type'); // 'pass', 'hold', 'pending', 'second-check'
  const startDate = searchParams.get('start_date');
  const endDate = searchParams.get('end_date');
  const filterTitle = {
    pass: 'Passed QC Entries',
    hold: 'QC Entries On Hold',
    pending: 'Pending QC Entries',
    'second-check': 'Second Check Pending',
  }[filterType] || 'QC Entries';

  useEffect(() => {
    fetchFilteredEntries();
  }, [filterType, startDate, endDate]);

  const fetchFilteredEntries = async () => {
    try {
      const response = await api.get('/qc');
      let filtered = response.data;

      // Filter by date range if provided
      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        filtered = filtered.filter((qc) => {
          const qcDate = new Date(qc.created_timestamp);
          return qcDate >= start && qcDate <= end;
        });
      }
      
      switch (filterType) {
        case 'pass':
          filtered = filtered.filter((qc) => qc.qc_status === 'PASS');
          break;
        case 'hold':
          filtered = filtered.filter((qc) => qc.qc_status === 'HOLD');
          break;
        case 'pending':
          filtered = filtered.filter((qc) => qc.qc_status === 'Pending');
          break;
        case 'second-check':
          filtered = filtered.filter((qc) => qc.second_check_status === 'Pending');
          break;
        default:
          break;
      }
      
      setEntries(filtered);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PASS':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            PASS
          </span>
        );
      case 'HOLD':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
            <XCircle className="w-3 h-3 mr-1" />
            HOLD
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="drill-down-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {filterTitle}
          </h1>
          <p className="text-muted-foreground">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'} found
            {startDate && endDate && (
              <span className="ml-2 text-sm font-medium">
                ({new Date(startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                {startDate !== endDate && ` - ${new Date(endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`})
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        {entries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg font-medium text-muted-foreground">No entries found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="hidden md:table-cell">Product</TableHead>
                <TableHead className="hidden md:table-cell">Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden lg:table-cell">Score</TableHead>
                <TableHead className="hidden lg:table-cell">Technician</TableHead>
                <TableHead className="hidden md:table-cell">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/history?asset=${entry.asset_id}`)}
                  data-testid={`drill-down-row-${entry.id}`}
                >
                  <TableCell className="font-medium">{entry.asset_id}</TableCell>
                  <TableCell>
                    <div>
                      <p className="truncate max-w-[150px]">{entry.customer_or_company_name}</p>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{entry.product_category}</TableCell>
                  <TableCell className="hidden md:table-cell">{entry.qc_event_type}</TableCell>
                  <TableCell>{getStatusBadge(entry.qc_status)}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {entry.qc_score !== null ? `${entry.qc_score}%` : '-'}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">{entry.created_by_technician}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {new Date(entry.created_timestamp).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default DrillDownPage;
